import { useEffect, useState, useRef } from "react";
import { FundCalendar } from "@/types";
import { useAtom } from "jotai";
import { SelectedReportingPeriodAtom } from "@/store";
import { generateFuturePeriods } from "@/utils/calendarUtils";
import { isPastDate } from "@/utils/dateUtils";

export interface DateEntry {
  date: Date;
  isNextPeriod: boolean;
  daysUntil: number;
}

export interface UpcomingReportingDatesProps {
  calendar: FundCalendar;
  numberOfDates?: number;
}

export const useUpcomingReportingDatesController = (
  props: UpcomingReportingDatesProps
) => {
  const { calendar, numberOfDates = 5 } = props;
  const [selectedReportingPeriod] = useAtom(SelectedReportingPeriodAtom);
  const [upcomingDates, setUpcomingDates] = useState<DateEntry[]>([]);
  const [baseDate, setBaseDate] = useState<Date | null>(null);
  const existingDatesRef = useRef<DateEntry[]>([]);

  useEffect(() => {
    // If no reporting period is selected, don't calculate dates
    if (!selectedReportingPeriod) {
      setUpcomingDates([]);
      setBaseDate(null);
      return;
    }

    // Parse the current period date from the selected reporting period
    // Assuming period is in format like "2023-06-30"
    const currentPeriodDate = new Date(selectedReportingPeriod.period);
    setBaseDate(currentPeriodDate);

    // If date parsing failed, exit
    if (isNaN(currentPeriodDate.getTime())) {
      console.error(
        "Invalid date format in reporting period:",
        selectedReportingPeriod.period
      );
      setUpcomingDates([]);
      return;
    }

    // Generate future dates
    const today = new Date();
    const futureDates = generateFuturePeriods(
      currentPeriodDate,
      calendar.intervalDefinition,
      numberOfDates
    );

    // Convert to DateEntry objects with additional information
    const entries = futureDates.map((date) => {
      // Calculate days until this date
      const timeDiff = date.getTime() - today.getTime();
      const daysUntil = Math.ceil(timeDiff / (1000 * 3600 * 24));

      // Check if this is the next period (first future date)
      const isNextPeriod =
        !isPastDate(date) &&
        (existingDatesRef.current.length === 0 ||
          existingDatesRef.current.every((d) => isPastDate(d.date)));

      return {
        date,
        isNextPeriod,
        daysUntil,
      };
    });

    existingDatesRef.current = entries;
    setUpcomingDates(entries);
  }, [selectedReportingPeriod, calendar.intervalDefinition, numberOfDates]);

  return {
    selectedReportingPeriod,
    upcomingDates,
    baseDate,
    calendar,
  };
};
